import React, { useState, useEffect, useRef } from 'react';
import '../Style/Journey.css';
import { useLanguage } from "../Context/Languagecontext";

// ── Image imports ──
import img1979 from '../Assets/baby.webp';
import img1997 from '../Assets/school.webp';
import img1999 from '../Assets/psg.jpg';
import img2000 from '../Assets/lab.webp';
import img2005 from '../Assets/lead1.jpg';
import img2010 from '../Assets/Service4.jpg';
import img2015 from '../Assets/m (62).jpg';
import img2024 from '../Assets/aa1.jpg';

export default function Journey() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('right');
  const [isVisible, setIsVisible] = useState(false);

  const contentRef = useRef(null);
  const sectionRef = useRef(null);

  const { t, lang } = useLanguage();
  const jData = t.journey;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(false);
          setTimeout(() => {
            setIsVisible(true);
          }, 50);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.25 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  /* ── Localized Structural Array Mapping ── */
  const journeyData = [
    {
      year: 1979,
      title: jData?.nodes?.node1?.title || 'Birth & Humble Beginnings',
      subtitle: jData?.nodes?.node1?.subtitle || 'Rooted in Vision & Values',
      description: jData?.nodes?.node1?.description || '',
      image: img1979,
    },
    {
      year: 1997,
      title: jData?.nodes?.node2?.title || 'School — Erode Hindu Kalvi Nilayam',
      subtitle: jData?.nodes?.node2?.subtitle || 'Character Forged in Discipline',
      description: jData?.nodes?.node2?.description || '',
      image: img1997,
    },
    {
      year: 1999,
      title: jData?.nodes?.node3?.title || 'PSG College of Arts & Science',
      subtitle: jData?.nodes?.node3?.subtitle || 'Passion Beyond Academics',
      description: jData?.nodes?.node3?.description || '',
      image: img1999,
    },
    {
      year: 2000,
      title: jData?.nodes?.node4?.title || 'Label Assistant — K.K. Balusamy & Co.',
      subtitle: jData?.nodes?.node4?.subtitle || 'Ground Level to the Top',
      description: jData?.nodes?.node4?.description || '',
      image: img2000,
    },
    {
      year: 2005,
      title: jData?.nodes?.node5?.title || 'Rise as Visionary Leader',
      subtitle: jData?.nodes?.node5?.subtitle || '"Never Say No" Mindset',
      description: jData?.nodes?.node5?.description || '',
      image: img2005,
    },
    {
      year: 2010,
      title: jData?.nodes?.node6?.title || 'Social Responsibility',
      subtitle: jData?.nodes?.node6?.subtitle || 'Empowering Communities',
      description: jData?.nodes?.node6?.description || '',
      image: img2010,
    },
    {
      year: 2015,
      title: jData?.nodes?.node7?.title || 'Managing Director',
      subtitle: jData?.nodes?.node7?.subtitle || 'Leadership Through Action',
      description: jData?.nodes?.node7?.description || '',
      image: img2015,
    },
    {
      year: 2026,
      title: jData?.nodes?.node8?.title || 'Legacy & Continuing Vision',
      subtitle: jData?.nodes?.node8?.subtitle || 'Impact Beyond Business',
      description: jData?.nodes?.node8?.description || '',
      image: img2024,
    },
  ];

  const handleSelect = (idx) => {
    if (idx === activeIndex || animating) return;
    setDirection(idx > activeIndex ? 'right' : 'left');
    setAnimating(true);

    setTimeout(() => {
      setActiveIndex(idx);
      setAnimating(false);
    }, 380);
  };

  const handleNext = () => {
    if (activeIndex < journeyData.length - 1) {
      handleSelect(activeIndex + 1);
    }
  };

  const handleBack = () => {
    if (activeIndex > 0) {
      handleSelect(activeIndex - 1);
    }
  };

  const active = journeyData[activeIndex];

  return (
    <section
      ref={sectionRef}
      className={`journey-section ${isVisible ? 'animate' : ''}${lang === 'ta' ? ' journey-section--tamil' : ''}`}
      id="journey"
    >
      {/* Section Header */}
      <div className="journey-header">
        <span className="journey-eyebrow">{jData?.eyebrow}</span>
        <h2 className="journey-title"> {jData?.title}</h2>
        <div className="journey-title-bar" />
      </div>

      {/* Timeline Rail */}
      <div className="timeline-wrapper">
        <div className="timeline-rail">
          {journeyData.map((item, idx) => (
            <button
              key={item.year}
              className={`timeline-node ${idx === activeIndex ? 'active' : ''} ${idx < activeIndex ? 'passed' : ''}`}
              onClick={() => handleSelect(idx)}
              aria-label={`Go to ${item.year}`}
            >
              <span className="node-star">
                {idx <= activeIndex ? '★' : '☆'}
              </span>
              <span className="node-year">{item.year}</span>
            </button>
          ))}

          {/* Progress line */}
          <div
            className="timeline-progress"
            style={{
              width: `${(activeIndex / (journeyData.length - 1)) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Year Badge + Back + Next */}
      <div className="journey-badge-row">
        <div className="journey-year-badge">
          <button
            className="badge-back"
            onClick={handleBack}
            disabled={activeIndex === 0}
            aria-label={jData?.prevLabel || "Previous year"}
          >
            ‹
          </button>

          <span>{active.year}</span>

          <button
            className="badge-next"
            onClick={handleNext}
            disabled={activeIndex === journeyData.length - 1}
            aria-label={jData?.nextLabel || "Next year"}
          >
            ›
          </button>
        </div>
      </div>

      {/* Content Card */}
      <div className="journey-content-wrap">
        <div
          ref={contentRef}
          className={`journey-card ${
            animating ? `exit-${direction}` : 'enter'
          }`}
        >
          {/* Image block */}
          <div className="journey-image-block">
            <div className="journey-image-inner">
              <div className="image-year-overlay">{active.year}</div>

              {active.image ? (
                <img src={active.image} alt={active.title} />
              ) : (
                <div className="image-placeholder-icon">
                  <svg
                    viewBox="0 0 80 80"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="40"
                      cy="28"
                      r="14"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    />
                    <path
                      d="M10 72c0-16.569 13.431-30 30-30s30 13.431 30 30"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}
            </div>

            <div className="image-caption">{active.subtitle}</div>
          </div>

          {/* Text */}
          <div className="journey-text-block">
            <h3 className="journey-card-title">{active.title}</h3>

            <div className="journey-card-divider" />

            <p className="journey-card-desc">{active.description}</p>

            {/* Pagination dots */}
            <div className="journey-dots">
              {journeyData.map((_, idx) => (
                <button
                  key={idx}
                  className={`dot ${idx === activeIndex ? 'active' : ''}`}
                  onClick={() => handleSelect(idx)}
                  aria-label={`${jData?.stepLabel || "Step"} ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}